import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Switch from "@material-ui/core/Switch";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
  loadingBox: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(3),
  },
  field: {
    marginTop: theme.spacing(2),
  },
}));

// Configura a validação automática de contatos contra o WhatsApp real, que
// roda sozinha em segundo plano (bem devagar, respeitando o rate limit) e,
// se habilitado, apaga quem der inválido — mas nunca quem já tem alguma
// conversa/ticket registrado, esses ficam preservados sempre.
const ContactValidationSettingsModal = ({ open, onClose }) => {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [enabled, setEnabled] = useState(false);
  const [deleteInvalid, setDeleteInvalid] = useState(true);
  const [batchSize, setBatchSize] = useState(5);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/validation-settings");
      setEnabled(!!data.enabled);
      setDeleteInvalid(!!data.deleteInvalid);
      setBatchSize(data.batchSize || 5);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchSettings();
  }, [open]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put("/contacts/validation-settings", {
        enabled,
        deleteInvalid,
        batchSize: Number(batchSize),
      });
      toast.success("Configuração salva com sucesso!");
      onClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Configurar validação automática</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box className={classes.loadingBox}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Quando habilitado, o sistema valida sozinho, aos poucos, os contatos que
              ainda não foram verificados contra o WhatsApp real — sem precisar clicar em
              nada. Contatos que já têm alguma conversa/ticket registrado nunca são
              apagados, mesmo que a validação dê inválida.
            </Typography>

            <FormControlLabel
              className={classes.field}
              control={
                <Switch
                  checked={enabled}
                  onChange={e => setEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label="Validar automaticamente em segundo plano"
            />

            <br />

            <FormControlLabel
              className={classes.field}
              control={
                <Switch
                  checked={deleteInvalid}
                  onChange={e => setDeleteInvalid(e.target.checked)}
                  color="primary"
                  disabled={!enabled}
                />
              }
              label="Excluir automaticamente quem der inválido (sem histórico)"
            />

            <TextField
              className={classes.field}
              label="Contatos verificados por vez (a cada ~1 min)"
              type="number"
              value={batchSize}
              onChange={e => setBatchSize(e.target.value)}
              inputProps={{ min: 1, max: 20 }}
              variant="outlined"
              size="small"
              fullWidth
              disabled={!enabled}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" variant="outlined">
          Fechar
        </Button>
        {!loading && (
          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={saving}
          >
            {saving ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ContactValidationSettingsModal;
